import { useState } from 'react';

import { ChevronDown, ChevronUp, LoaderCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import AISparkles from '@/components/common/AISparkles';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import { Button } from '@/components/shadcn/button';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import cn from '@/lib/utils/cn';

import AnalysisActions from './AnalysisActions/AnalysisActions';
import AnalysisBadges from './AnalysisBadges/AnalysisBadges';
import AnalysisNotInsurance from './AnalysisNotInsurance';
import AnalysisSummary from './AnalysisSummary';

export default function AnalysisSection() {
  const { t } = useTranslation();
  const {
    party: { status },
    analysis: {
      analysis,
      triage,
      isAnalyzing,
      isReAnalyzing,
      isPollingAnalysis,
      hasAnalysisFailed,
    },
    reAnalyze,
  } = useEmail();
  const { mode } = useEmailClient();

  const [isOpen, setIsOpen] = useState(mode !== 'Compose');

  const hasAnalysis = Boolean(analysis?.finished_at);
  const isNotInsurance =
    Boolean(triage.classification) &&
    triage.classification !== 'INSURANCE_RELATED';

  const canReanalyze = hasAnalysis;

  const activeSection = (() => {
    if (isAnalyzing) return 'loading';
    if (hasAnalysis) return 'analysis';
    if (isPollingAnalysis) return 'loading';

    if (status !== 'Selected') return 'select-party';
    if (isNotInsurance) return 'not-insurance';
    if (hasAnalysisFailed) return 'failed';

    return 'loading';
  })();

  return (
    <div className="flex flex-col gap-2 border-b p-2">
      <div className="flex w-full items-center justify-between">
        <div className="text-foreground flex h-7 items-center gap-1.5 text-sm">
          <AISparkles />

          {t('AI_INSIGHT_TITLE')}
        </div>

        <div className="flex items-center gap-1">
          <AnalysisBadges />

          {canReanalyze && (
            <Button
              variant="secondary"
              size="icon-sm"
              onClick={reAnalyze}
              disabled={isAnalyzing || isPollingAnalysis}
            >
              <RefreshCw className={cn({ ['animate-spin']: isReAnalyzing })} />
            </Button>
          )}

          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen && <ChevronDown className="size-4" />}
            {!isOpen && <ChevronUp className="size-4" />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-1">
          {activeSection === 'loading' && (
            <div className="flex items-center justify-center">
              <div className="text-muted-foreground my-4 flex items-center gap-2 text-sm">
                <LoaderCircle className="text-primary-500 size-4 animate-spin" />

                <span>{t('ANALYZING_EMAIL')}</span>
              </div>
            </div>
          )}

          {activeSection === 'analysis' && (
            <div className="flex flex-col gap-2">
              <AnalysisSummary />

              <AnalysisActions />
            </div>
          )}

          {activeSection === 'not-insurance' && <AnalysisNotInsurance />}

          {activeSection === 'select-party' && (
            <div className="text-muted-foreground text-xs">
              {t('EMAIL_ANALYSIS_WAIT_FOR_PARTY_OR_CREATE')}
            </div>
          )}

          {activeSection === 'failed' && (
            <div className="text-muted-foreground text-xs">
              {t('EMAIL_ANALYSIS_FAILED')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
